import { z } from "zod";
// The response shapes of the Phase 2.6 domain routes. Every customer view is a STRICT object: a key
// that is not listed here fails the parse and the route answers 500, so a field added to a view
// builder later cannot reach a customer without someone naming it here too. That is fail-closed on
// purpose: leaking is worse than failing. `apps/api` has a drift test that compares each shape's keys
// with the key list the application package exports next to the view builder (this package cannot
// import the application package).
//
// Values that are codes (statuses, kinds, units) are plain strings here. Their allowed values are
// owned by the domain, and listing them twice would only create a second place to drift.
const id = z.string().min(1).max(64);
const code = z.string().min(1).max(64);
const text = z.string();
const nullableText = z.string().nullable();
const instant = z.date();
const nullableInstant = z.date().nullable();
const count = z.number().int();
const nullableNumber = z.number().nullable();
// --- conversation -----------------------------------------------------------------------------
export const customerConversationViewSchema = z.strictObject({
    id,
    statusCode: code,
    openHandoffStatusCode: code.nullable(),
    createdAt: instant,
});
// --- concierge request ------------------------------------------------------------------------
export const customerIndicativeBudgetSchema = z.strictObject({
    amountMinor: count,
    currency: code,
});
export const customerRequestTimelineEntrySchema = z.strictObject({
    statusCode: code,
    occurredAt: instant,
    message: nullableText,
});
// CHT-752: what staff approved for the customer, and the files they shared. Nothing else of the human work: no evidence, no supplier, no
// contact, no reference, no storage key, no staff id.
export const customerRequestUpdateSchema = z.strictObject({
    id,
    kind: code,
    text,
    // The explicit CORRECTED marker of a correction of a confirmation.
    isCorrection: z.boolean(),
    createdAt: instant,
});
export const customerRequestAttachmentSchema = z.strictObject({
    id,
    displayName: text,
    contentType: code,
    sizeBytes: count,
    sharedAt: instant,
});
// CHT-754: the signed download of ONE shared file of the customer's own request. The URL lives about a minute and the answer is never
// cached. No storage key, no staff id, no visibility, no share reason.
export const customerRequestAttachmentDownloadSchema = z.strictObject({
    id,
    displayName: text,
    contentType: code,
    sizeBytes: count,
    url: z.string().min(1),
    // A `Date` on the server, an ISO string on the wire: the same schema serves the route and the client.
    expiresAt: z.union([z.date(), z.iso.datetime({ offset: true }).transform((v) => new Date(v))]),
});
export const customerConciergeRequestViewSchema = z.strictObject({
    id,
    requestType: code,
    statusCode: code,
    title: text,
    partySize: count.nullable(),
    dateFrom: nullableText,
    dateTo: nullableText,
    preferredTime: nullableText,
    timeZone: nullableText,
    locationText: nullableText,
    specialRequests: nullableText,
    indicativeBudget: customerIndicativeBudgetSchema.nullable(),
    requirementsVersion: count,
    // The customer's own typed details. The domain validates their keys per request type on write.
    requirementsDetails: z.record(z.string(), z.unknown()),
    neededBy: nullableInstant,
    pendingQuestion: nullableText,
    customerStatusSummary: nullableText,
    paymentRequestId: id.nullable(),
    conversationId: id.nullable(),
    createdAt: instant,
    submittedAt: nullableInstant,
    confirmedAt: nullableInstant,
    completedAt: nullableInstant,
    closedAt: nullableInstant,
    updatedAt: instant,
    timeline: z.array(customerRequestTimelineEntrySchema),
    updates: z.array(customerRequestUpdateSchema),
    attachments: z.array(customerRequestAttachmentSchema),
    /** `true` when older items were left out of the newest window. The newest are never left out. */
    updatesTruncated: z.boolean(),
    attachmentsTruncated: z.boolean(),
});
// --- action center ----------------------------------------------------------------------------
export const customerActionItemSchema = z.strictObject({
    id: text,
    sourceKind: code,
    sourceId: id,
    bucket: code,
    statusCode: code,
    title: nullableText,
    updatedAt: instant,
});
export const actionCenterViewSchema = z.strictObject({
    items: z.array(customerActionItemSchema),
});
// --- catalog ----------------------------------------------------------------------------------
export const customerCatalogPricingViewSchema = z.strictObject({
    model: code,
    currency: nullableText,
    amountMinor: nullableNumber,
    minAmountMinor: nullableNumber,
    maxAmountMinor: nullableNumber,
    unit: code.nullable(),
    terms: nullableText,
});
export const customerCatalogLocationSchema = z.strictObject({
    addressText: nullableText,
    locality: nullableText,
    region: nullableText,
    countryCode: nullableText,
    latitude: nullableNumber,
    longitude: nullableNumber,
});
export const customerCatalogCategorySchema = z.strictObject({
    code,
    name: text,
    isPrimary: z.boolean(),
});
export const customerCatalogTagSchema = z.strictObject({
    namespace: code,
    code,
    label: text,
});
export const customerCatalogMediaSchema = z.strictObject({
    kind: code,
    storageObjectPath: text,
    altText: nullableText,
    caption: nullableText,
    credit: nullableText,
    position: count,
    isCover: z.boolean(),
});
export const customerCatalogPersonSchema = z.strictObject({
    slug: text,
    displayName: text,
    personKind: code,
    shortBio: nullableText,
    role: code,
    position: count,
});
export const customerCatalogItemViewSchema = z.strictObject({
    id,
    itemType: code,
    name: text,
    slug: text,
    shortDescription: nullableText,
    description: nullableText,
    location: customerCatalogLocationSchema,
    timeZone: nullableText,
    pricing: customerCatalogPricingViewSchema,
    fulfilmentMode: code,
    externalUrl: nullableText,
    categories: z.array(customerCatalogCategorySchema),
    tags: z.array(customerCatalogTagSchema),
    media: z.array(customerCatalogMediaSchema),
    people: z.array(customerCatalogPersonSchema),
    // The item type's own columns. Every subtype column is customer-visible by rule, and the shape
    // depends on `itemType`, so the keys are not listed here.
    details: z.record(z.string(), z.unknown()),
    publishedAt: instant,
});
// --- payment request --------------------------------------------------------------------------
export const customerPaymentRequestViewSchema = z.strictObject({
    id,
    status: code,
    amountMinor: count,
    currency: code,
    description: text,
    sourceKind: code,
    conciergeRequestId: id.nullable(),
    tripId: id.nullable(),
    itineraryItemId: id.nullable(),
    catalogItemId: id.nullable(),
    expiresAt: instant,
    createdAt: instant,
    statusChangedAt: instant,
    refundedAmountMinor: count,
    canStartCheckout: z.boolean(),
    // CHT-757: the customer-visible terms of an approved quote (`null` for a fixed price). Optional on the wire so an older fixture still parses.
    customerTerms: z.string().max(500).nullable().optional(),
});
// --- staff ------------------------------------------------------------------------------------
// Staff read everything the use case returns once it has authorized them, so the nested records are
// not re-listed key by key. The top-level shape is still strict, so a new part of a staff view has
// to be named here before it is served.
const staffRecord = z.record(z.string(), z.unknown());
export const staffHandoffViewSchema = z.strictObject({
    handoff: staffRecord,
    events: z.array(staffRecord),
});
export const staffHandoffQueueSchema = z.array(staffRecord);
export const staffConciergeRequestViewSchema = z.strictObject({
    request: staffRecord,
    internalNotes: z.array(staffRecord),
    transitions: z.array(staffRecord),
});
// The Phase 11 sections (CHT-759) have their own typed schemas in `payments.ts`; the top level stays strict.
export const staffPaymentRequestViewSchema = z.strictObject({
    request: staffRecord,
    quote: staffRecord.nullable(),
    refunds: z.array(staffRecord),
    observations: z.array(staffRecord),
    exceptions: z.array(staffRecord),
    transitions: z.array(staffRecord),
    providerCalls: z.array(staffRecord),
    amountSource: staffRecord.nullable(),
    reconciliationRuns: z.array(staffRecord),
    evidence: z.enum(["FAKE", "REAL", "NONE"]),
});
/** The outcome a command with no view returns: flat ids, states and codes (never request bodies). */
export const staffCommandOutcomeSchema = z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]));
